<template>
  <client-only>
    <Swiper
      ref="swiperRef"
      :dir="swiperDir"
      :modules="modules"
      :autoplay="autoplaySettings"
      :centeredSlides="centeredSlides"
      :speed="type === 'Brands' ? 4000 : 500"
      :loop="loop"
      :breakpoints="breakpoints"
      :effect="swiperEffect"
      :parallax="isParallax"
      :navigation="navigationSettings"
      :pagination="paginationSettings"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      :class="{ '!pb-[45px]': pagination }"
    >
      <SwiperSlide
        v-for="(slide, index) in normalizedData"
        :key="index"
        class="select-none"
      >
        <slot
          :slide="slide"
          :index="index"
          :activeIndex="activeIndex"
          :activeRealIndex="activeRealIndex"
        />
      </SwiperSlide>
      <div
        v-if="showDefaultNavigation"
        :class="['swiper-button-prev', navPrevClass]"
      ></div>
      <div
        v-if="showDefaultNavigation"
        :class="['swiper-button-next', navNextClass]"
      ></div>
    </Swiper>
  </client-only>
</template>

<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { Swiper, SwiperSlide } from "swiper/vue";

// RTL (Arapça) locale'de slider'lar sağdan-sola akar: autoplay yönü ve slide
// sırası Swiper tarafından otomatik ters çevrilir.
const { locale } = useI18n();
const swiperDir = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
import {
  Autoplay,
  Navigation,
  Pagination,
  Scrollbar,
  EffectFade,
  EffectCube,
  EffectCoverflow,
  EffectFlip,
  EffectCards,
  EffectCreative,
  Parallax,
} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/effect-fade";
import "swiper/css/effect-cube";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-flip";
import "swiper/css/effect-cards";
import "swiper/css/effect-creative";
import "swiper/css/parallax";

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  speed: {
    type: Number,
    default: 500,
  },
  navigation: {
    type: [Boolean, Object],
    default: false,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  autoplayTime: {
    type: [Number, String],
    default: 6000,
  },
  centeredSlides: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: false,
  },
  effect: {
    type: String,
    default: "", // Example: 'fade', 'cube', 'coverflow', 'flip', 'cards', 'creative', 'parallax'
  },
  type: {
    type: String,
    default: "",
  },
  per: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["slideChange"]);

const normalizedEffect = computed(() =>
  (props.effect || "").toLowerCase().trim()
);
const isParallax = computed(() => normalizedEffect.value === "parallax");
const swiperEffect = computed(() =>
  isParallax.value ? "slide" : normalizedEffect.value || undefined
);

const effectModules = {
  fade: EffectFade,
  cube: EffectCube,
  coverflow: EffectCoverflow,
  flip: EffectFlip,
  cards: EffectCards,
  creative: EffectCreative,
  parallax: Parallax,
};

const modules = computed(() => {
  const baseModules = [Autoplay, Navigation, Pagination, Scrollbar];
  const effectModule = effectModules[normalizedEffect.value];

  if (effectModule) {
    baseModules.push(effectModule);
  }

  return baseModules;
});

const navId = `plugins-carousel-${Math.random().toString(36).slice(2, 9)}`;
const navPrevClass = `${navId}-prev`;
const navNextClass = `${navId}-next`;

const showDefaultNavigation = computed(() => props.navigation === true);

const navigationSettings = computed(() => {
  if (!props.navigation) return false;
  if (typeof props.navigation === "object") return props.navigation;

  return {
    enabled: true,
    prevEl: `.${navPrevClass}`,
    nextEl: `.${navNextClass}`,
  };
});

const autoplayDelay = computed(() => {
  const parsed = Number(props.autoplayTime);
  return Number.isFinite(parsed) ? parsed : 6000;
});

const swiperRef = ref(null);
const activeIndex = ref(0);
const activeRealIndex = ref(0);

const syncActiveIndexes = (swiper) => {
  const currentActive = swiper?.activeIndex ?? 0;
  activeIndex.value = currentActive;
  activeRealIndex.value = swiper?.realIndex ?? currentActive;
};

const emitSlideChange = (swiper) => {
  if (!swiper) return;
  syncActiveIndexes(swiper);
  emit("slideChange", {
    activeIndex: activeIndex.value,
    realIndex: activeRealIndex.value,
    previousIndex: swiper.previousIndex ?? null,
    swiper,
  });
};

const onSwiper = (swiper) => {
  swiperRef.value = swiper;
  emitSlideChange(swiper);
};

const onSlideChange = () => {
  if (!swiperRef.value) return;
  emitSlideChange(swiperRef.value);
};

const autoplaySettings = computed(() =>
  props.autoplay
    ? {
        delay: props.type === "Brands" ? 0 : autoplayDelay.value,
        disableOnInteraction: false,
      }
    : false
);

const paginationSettings = computed(() =>
  props.pagination
    ? {
        clickable: true,
        dynamicBullets: true,
      }
    : false
);

const breakpoints = computed(() => {
  switch (props.type) {
    case "Card":
      return {
        0: {
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 16,
          slidesPerView: 1.2,
          spaceBetween: 16,
          centeredSlides: false,
        },
        768: {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          slidesPerView: 2.4,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1280: {
          slidesPerView: props.per || 4,
          spaceBetween: 32,
        },
      };
    case "Product":
      return {
        0: {
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 16,
          slidesPerView: 1.2,
          spaceBetween: 16,
          centeredSlides: false,
        },
        768: {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          slidesPerView: 2.4,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1280: {
          slidesPerView: props.per || 4,
          spaceBetween: 32,
        },
      };
    case "Packages":
      return {
        0: {
          slidesPerView: 1.1,
          spaceBetween: 16,
          centeredSlides: false,
        },
        768: {
          slidesPerView: 2.2,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1280: {
          slidesPerView: 3,
          spaceBetween: 32,
        },
      };
    case "Gallery":
      return {
        0: {
          slidesPerView: 1,
          spaceBetween: 16,
          centeredSlides: false,
        },
        768: {
          slidesPerView: 2.3,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3.4,
          spaceBetween: 16,
        },
        1280: {
          slidesPerView: 3.9,
          spaceBetween: 16,
        },
      };
    case "Blog":
      return {
        0: {
          slidesPerView: 1.15,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: props.per,
          spaceBetween: 32,
        },
      };
    case "Brands":
      return {
        0: {
          slidesPerView: 2.5,
          spaceBetween: 16,
          centeredSlides: true,
          allowTouchMove: false,
        },
        768: {
          slidesPerView: 3.5,
          spaceBetween: 16,
          centeredSlides: true,
          allowTouchMove: false,
        },
        1280: {
          slidesPerView: 5,
          spaceBetween: 32,
          centeredSlides: true,
          allowTouchMove: false,
        },
        1536: {
          slidesPerView: 6,
          spaceBetween: 32,
          centeredSlides: true,
          allowTouchMove: false,
        },
      };
    case "Deck":
      return {
        0: {
          slidesPerView: 1,
          centeredSlides: true,
        },
        768: {
          slidesPerView: 1,
        },
        1024: {
          slidesPerView: 1,
        },
      };
    default:
      return {
        0: {
          slidesPerView: props.per || 1,
          spaceBetween: 16,
        },
      };
  }
});

// Loop açıkken slayt sayısı yetmezse uyarıyı önlemek için çoğalt
const maxSlidesPerView = computed(() => {
  const bp = breakpoints.value || {};
  const views = Object.values(bp)
    .map((item) => Number(item?.slidesPerView) || 1)
    .filter(Boolean);
  const base = views.length ? Math.max(...views) : props.per || 1;
  return base || 1;
});

const normalizedData = computed(() => {
  if (!props.loop) return props.data;
  const minSlides = Math.max(2, Math.ceil(maxSlidesPerView.value * 2));
  if (!props.data || props.data.length >= minSlides) return props.data;

  const cloned = [];
  while (cloned.length < minSlides) {
    cloned.push(...props.data);
  }

  return cloned;
});
</script>
